import React from "react";
import { formatReviewAverage } from "@/src/shared/utils/formatReviewAverage";
import type { IListing } from "@/src/shared/types/listing";
import { cn } from "@/src/lib/utils";
import { listingService } from "../services";
import GuestFavouriteLaurels from "./GuestFavouriteLaurels";

interface IListingTitleBlockProps {
  listing: IListing;
  /** Centred, as the app's sheet over the photo (mobile); left-aligned on desktop. */
  centered?: boolean;
  /** The title is the page's h1 (only one copy may be). */
  asHeading?: boolean;
  /** Desktop shows the title above the photos, so this block starts at the property line. */
  showTitle?: boolean;
  /** Play the Guest Favourite laurels' opening animation (the app's Lottie, phone). */
  animateFavourite?: boolean;
  onShowReviews: () => void;
}

/**
 * The app's title block: the title, the property line ("Room in an Apartment"), the
 * specs line, then the Guest Favourite card or the rating line.
 */
const ListingTitleBlock: React.FC<IListingTitleBlockProps> = ({
  listing,
  centered = false,
  asHeading = false,
  showTitle = true,
  animateFavourite = false,
  onShowReviews,
}) => {
  const TitleTag = asHeading ? "h1" : "p";
  const propertyLine = listingService.propertyTypeLabel(
    listing.property_type?.name ?? "",
    listing.place_type ?? "",
  );
  const specs = listingService.specsSummary(listing);
  const count = listing.reviews_count ?? 0;
  const average = formatReviewAverage(listing.reviews_avg ?? 0);
  const favourite = listingService.guestFavouriteBadge(listing.badges);

  return (
    <div className={cn(centered && "text-center")}>
      {showTitle && (
        <TitleTag
          className={cn(
            "font-semibold tracking-[0.2px] text-ink",
            centered
              ? "text-2xl leading-7"
              : "text-24 leading-[30px] md:text-30 md:leading-8",
          )}
        >
          {listing.title}
        </TitleTag>
      )}
      {propertyLine && (
        <p
          className={cn(
            "text-sm font-medium text-ink-secondary md:text-base",
            showTitle && "mt-2",
          )}
        >
          {propertyLine}
        </p>
      )}
      {specs && (
        <p className="mt-1 text-sm font-medium text-ink-secondary md:text-base">
          {specs}
        </p>
      )}

      {favourite ? (
        <button
          type="button"
          onClick={onShowReviews}
          className={cn(
            "mt-4 grid w-full max-w-[354px] grid-cols-[1fr_2fr_1fr] items-center rounded-xl border border-[#EBEBEB] px-4 py-3 text-left transition-shadow hover:shadow-md",
            centered && "mx-auto",
          )}
        >
          <span className="flex flex-col items-center border-r border-[#D9D9D9] pr-2">
            <span className="text-15 font-bold text-ink-black">{average}</span>
            <span className="mt-0.5 flex gap-px">
              {[1, 2, 3, 4, 5].map((n) => (
                <img
                  key={n}
                  src="/icons/listing/listing_badge_star.svg"
                  alt=""
                  className={cn(
                    "h-3 w-3",
                    n > Math.round(listing.reviews_avg ?? 0) && "opacity-20",
                  )}
                />
              ))}
            </span>
          </span>
          <GuestFavouriteLaurels
            label={favourite.name || "Guest Favorite"}
            animated={animateFavourite}
          />
          <span className="flex flex-col items-center border-l border-[#D9D9D9] pl-2">
            <span className="text-15 font-semibold text-ink-black">
              {count.toLocaleString("en-US")}
            </span>
            <span className="text-sm font-semibold text-ink-black underline">
              Reviews
            </span>
          </span>
        </button>
      ) : (
        count > 0 && (
          <p
            className={cn(
              "mt-2 flex items-center gap-1 text-sm font-medium text-ink-secondary",
              centered && "justify-center",
            )}
          >
            <img
              src="/icons/listing/listing_rating_star.svg"
              alt=""
              className="h-3 w-3"
            />
            {average} ·{" "}
            <button
              type="button"
              onClick={onShowReviews}
              className="underline underline-offset-2 hover:text-ink"
            >
              {listingService.reviewCountLabel(count)}
            </button>
          </p>
        )
      )}
    </div>
  );
};

export default ListingTitleBlock;
